import React, { useEffect, useMemo, useState } from "react";
import {
  Stack,
  Group,
  TextInput,
  NumberInput,
  Select,
  MultiSelect,
  Button,
  Text,
  Anchor,
  Divider,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import {
  CreateTaxRateRequest,
  EditTaxRateRequest,
  TaxJurisdictionLevel,
  TaxRate,
} from "../../types/taxRates";

interface Props {
  initialRate: TaxRate | null;
  availableProducts: Array<{ id: string; name: string; category?: string }>;
  availableServices: Array<{
    id: string;
    name: string;
    service_type?: string;
  }>;
  onSubmit: (data: CreateTaxRateRequest | EditTaxRateRequest) => void;
  onCancel: () => void;
  isSaving: boolean;
  saveError?: string | null;
}

const JURISDICTION_OPTIONS: { value: TaxJurisdictionLevel; label: string }[] = [
  { value: "State", label: "State" },
  { value: "County", label: "County" },
  { value: "City", label: "City" },
  { value: "Special_District", label: "Special District" },
  { value: "Other", label: "Other" },
];

const normalizeIsoDate = (s: string | null | undefined): string | null => {
  if (!s) return null;
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const todayIso = (): string => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function TaxRateForm({
  initialRate,
  availableProducts,
  availableServices,
  onSubmit,
  onCancel,
  isSaving,
  saveError,
}: Props) {
  const [name, setName] = useState(initialRate?.name || "");
  const [ratePercent, setRatePercent] = useState<number | string>(
    initialRate ? initialRate.rate * 100 : "",
  );
  const [jurisdictionLevel, setJurisdictionLevel] =
    useState<TaxJurisdictionLevel>(initialRate?.jurisdiction_level || "State");
  const [jurisdictionName, setJurisdictionName] = useState(
    initialRate?.jurisdiction_name || "",
  );
  const [effectiveStartDate, setEffectiveStartDate] = useState<string | null>(
    normalizeIsoDate(initialRate?.effective_start_date) || todayIso(),
  );
  const [effectiveEndDate, setEffectiveEndDate] = useState<string | null>(
    normalizeIsoDate(initialRate?.effective_end_date || null),
  );
  const [productIds, setProductIds] = useState<string[]>(
    initialRate?.product_ids || [],
  );
  const [serviceIds, setServiceIds] = useState<string[]>(
    initialRate?.salon_service_ids || [],
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setName(initialRate?.name || "");
    setRatePercent(initialRate ? initialRate.rate * 100 : "");
    setJurisdictionLevel(initialRate?.jurisdiction_level || "State");
    setJurisdictionName(initialRate?.jurisdiction_name || "");
    setEffectiveStartDate(
      normalizeIsoDate(initialRate?.effective_start_date) || todayIso(),
    );
    setEffectiveEndDate(
      normalizeIsoDate(initialRate?.effective_end_date || null),
    );
    setProductIds(initialRate?.product_ids || []);
    setServiceIds(initialRate?.salon_service_ids || []);
    setValidationError(null);
  }, [initialRate]);

  const productOptions = useMemo(
    () =>
      availableProducts
        .filter((p) => p.id != null)
        .map((p) => ({
          value: String(p.id),
          label: p.category ? `${p.name} (${p.category})` : p.name,
        })),
    [availableProducts],
  );

  const serviceOptions = useMemo(
    () =>
      availableServices
        .filter((s) => s.id != null)
        .map((s) => ({
          value: String(s.id),
          label: s.service_type ? `${s.name} (${s.service_type})` : s.name,
        })),
    [availableServices],
  );

  const safeProductIds = useMemo(() => {
    const validSet = new Set(productOptions.map((o) => o.value));
    return productIds.filter((id) => id != null && validSet.has(String(id)));
  }, [productIds, productOptions]);

  const safeServiceIds = useMemo(() => {
    const validSet = new Set(serviceOptions.map((o) => o.value));
    return serviceIds.filter((id) => id != null && validSet.has(String(id)));
  }, [serviceIds, serviceOptions]);

  const handleSelectAllProducts = () => {
    setProductIds(
      availableProducts.filter((p) => p.id != null).map((p) => p.id),
    );
  };
  const handleClearProducts = () => setProductIds([]);
  const handleSelectAllServices = () => {
    setServiceIds(
      availableServices.filter((s) => s.id != null).map((s) => s.id),
    );
  };
  const handleClearServices = () => setServiceIds([]);

  const handleSubmit = () => {
    setValidationError(null);

    if (!name.trim()) {
      setValidationError("Name is required");
      return;
    }
    const rateNumber =
      typeof ratePercent === "number" ? ratePercent : parseFloat(ratePercent);
    if (isNaN(rateNumber) || rateNumber < 0) {
      setValidationError("Rate must be a non-negative number");
      return;
    }
    if (!effectiveStartDate) {
      setValidationError("Effective start date is required");
      return;
    }
    if (effectiveEndDate && effectiveEndDate <= effectiveStartDate) {
      setValidationError("Effective end date must be after start date");
      return;
    }

    const rateFraction = rateNumber / 100;

    const data: CreateTaxRateRequest = {
      name: name.trim(),
      rate: rateFraction,
      jurisdiction_level: jurisdictionLevel,
      jurisdiction_name: jurisdictionName.trim() || null,
      effective_start_date: effectiveStartDate,
      effective_end_date: effectiveEndDate,
      product_ids: productIds,
      salon_service_ids: serviceIds,
    };
    onSubmit(data);
  };

  return (
    <Stack gap="md">
      <TextInput
        label="Name"
        placeholder="MA State Sales Tax"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        required
      />

      <Group grow>
        <NumberInput
          label="Rate (%)"
          placeholder="6.25"
          value={ratePercent}
          onChange={(val) => setRatePercent(val ?? "")}
          min={0}
          max={100}
          decimalScale={3}
          required
        />
        <Select
          label="Jurisdiction Level"
          data={JURISDICTION_OPTIONS}
          value={jurisdictionLevel}
          onChange={(val) =>
            val && setJurisdictionLevel(val as TaxJurisdictionLevel)
          }
          allowDeselect={false}
        />
      </Group>

      <TextInput
        label={
          <Group gap={4} align="center">
            <Text size="sm" fw={500}>
              Jurisdiction Name
            </Text>
            <Tooltip
              label='Used for reporting. Specify either the state or local municipality. Ex: "MA - Massachusetts", "Suffolk County".'
              position="top"
              multiline
              w={280}
              withArrow
            >
              <IconInfoCircle
                size={14}
                style={{ color: "var(--mantine-color-dimmed)", cursor: "help" }}
              />
            </Tooltip>
          </Group>
        }
        placeholder="MA - Massachusetts, Suffolk County, etc."
        value={jurisdictionName}
        onChange={(e) => setJurisdictionName(e.currentTarget.value)}
      />

      <Group grow>
        <DateInput
          label="Effective Start Date"
          value={effectiveStartDate}
          onChange={(val) => setEffectiveStartDate(normalizeIsoDate(val))}
          required
        />
        <DateInput
          label="Effective End Date"
          description="Leave empty for indefinite"
          value={effectiveEndDate}
          onChange={(val) => setEffectiveEndDate(normalizeIsoDate(val))}
          clearable
        />
      </Group>

      <Divider my="xs" />

      <Stack gap="xs">
        <Group justify="space-between" align="flex-end">
          <Text fw={500} size="sm">
            Applicable Products
          </Text>
          <Group gap="xs">
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={handleSelectAllProducts}
            >
              Select all ({availableProducts.length})
            </Anchor>
            <Text size="xs" c="dimmed">
              |
            </Text>
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={handleClearProducts}
            >
              Clear
            </Anchor>
          </Group>
        </Group>
        <MultiSelect
          placeholder={
            productIds.length === 0
              ? "No products selected"
              : `${productIds.length} selected`
          }
          data={productOptions}
          value={safeProductIds}
          onChange={setProductIds}
          searchable
          clearable
          nothingFoundMessage="No products found"
          maxDropdownHeight={220}
        />
      </Stack>

      <Stack gap="xs">
        <Group justify="space-between" align="flex-end">
          <Text fw={500} size="sm">
            Applicable Services
          </Text>
          <Group gap="xs">
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={handleSelectAllServices}
            >
              Select all ({availableServices.length})
            </Anchor>
            <Text size="xs" c="dimmed">
              |
            </Text>
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={handleClearServices}
            >
              Clear
            </Anchor>
          </Group>
        </Group>
        <MultiSelect
          placeholder={
            serviceIds.length === 0
              ? "No services selected"
              : `${serviceIds.length} selected`
          }
          data={serviceOptions}
          value={safeServiceIds}
          onChange={setServiceIds}
          searchable
          clearable
          nothingFoundMessage="No services found"
          maxDropdownHeight={220}
        />
      </Stack>

      {(validationError || saveError) && (
        <Text c="red" size="sm">
          {validationError || saveError}
        </Text>
      )}

      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={isSaving} color="teal">
          {initialRate ? "Save Changes" : "Create Tax Rate"}
        </Button>
      </Group>
    </Stack>
  );
}
